import { API_PATH } from 'constants/general';
import { LS_AUTH_DATA_KEY } from 'constants/user';
import { StorageRequestUrlPart } from 'state/contexts/storageRequestContext/storageRequest.types';
import { StorageRequest, StorageRequestPayload } from 'types/storageRequest';
import { getRequestConfig, handleResponse } from './apiService';
import { getLocalStorageData } from './utilsService';

type getStorageRequests = (type: StorageRequestUrlPart) => Promise<StorageRequest[]>;
export const getStorageRequests: getStorageRequests = async (type) => {
  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);

  const config = getRequestConfig({
    method: 'GET',
    token: localStorageAuthData?.token,
  });

  return fetch(`${API_PATH}/users/me/${type}/active`, config).then(handleResponse);
};

type createStorageRequest = (
  storageRequest: StorageRequestPayload,
) => Promise<StorageRequest>;
export const createStorageRequest: createStorageRequest = async (storageRequest) => {
  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);

  const config = getRequestConfig({
    method: 'POST',
    token: localStorageAuthData?.token,
    body: JSON.stringify(storageRequest),
  });

  return fetch(`${API_PATH}/users/me/sentStorageRequests`, config).then(handleResponse);
};

type archiveStorageRequest = (
  storageRequestId: string,
  type: StorageRequestUrlPart,
) => Promise<StorageRequest>;
export const archiveStorageRequest: archiveStorageRequest = async (
  storageRequestId,
  type,
) => {
  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);

  const config = getRequestConfig({
    method: 'POST',
    token: localStorageAuthData?.token,
  });

  return fetch(`${API_PATH}/users/me/${type}/${storageRequestId}/archive`, config).then(
    handleResponse,
  );
};

type getArchivedStorageRequests = (
  type: StorageRequestUrlPart,
) => Promise<StorageRequest[]>;
export const getArchivedStorageRequests: getArchivedStorageRequests = async (type) => {
  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);

  const config = getRequestConfig({
    method: 'GET',
    token: localStorageAuthData?.token,
  });

  return fetch(`${API_PATH}/users/me/${type}/archived`, config).then(handleResponse);
};

type confirmStorageRequest = (
  storageRequestId: string,
  action: 'accept' | 'decline',
) => Promise<StorageRequest>;
export const confirmStorageRequest: confirmStorageRequest = async (
  storageRequestId,
  action,
) => {
  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);

  const config = getRequestConfig({
    method: 'POST',
    token: localStorageAuthData?.token,
  });

  return fetch(
    `${API_PATH}/users/me/receivedStorageRequests/${storageRequestId}/${action}`,
    config,
  ).then(handleResponse);
};

type cancelStorageRequest = (storageRequestId: string) => Promise<StorageRequest>;
export const cancelStorageRequest: cancelStorageRequest = async (storageRequestId) => {
  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);

  const config = getRequestConfig({
    method: 'DELETE',
    token: localStorageAuthData?.token,
  });

  return fetch(
    `${API_PATH}/users/me/receivedStorageRequests/${storageRequestId}`,
    config,
  ).then(handleResponse);
};

export const arrangeStorageRequestsByDate = (
  storageRequests: StorageRequest[],
): StorageRequest[] => {
  return storageRequests.sort((a, b) => {
    return +new Date(b.createdAt) - +new Date(a.createdAt);
  });
};

export const addTypeToStorageRequest = (
  storageRequest: StorageRequest,
  type: 'received' | 'sent',
): StorageRequest => ({ ...storageRequest, type });

export const addTypeToStorageRequests = (
  storageRequests: StorageRequest[],
  type: 'received' | 'sent',
): StorageRequest[] => storageRequests.map((request) => ({ ...request, type }));

type setStorageRequestToSeen = (storageRequestId: string) => Promise<StorageRequest>;
export const setStorageRequestToSeen: setStorageRequestToSeen = async (
  storageRequestId,
) => {
  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);

  const config = getRequestConfig({
    method: 'POST',
    token: localStorageAuthData?.token,
  });

  return fetch(
    `${API_PATH}/users/me/receivedStorageRequests/${storageRequestId}/markAsSeen`,
    config,
  ).then(handleResponse);
};
